// AdminLayout organism — shared admin shell with a collapsible burger nav.
import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const NAV_LINKS = [
  { to: "/admin", label: "Dashboard", end: true },
  { to: "/admin/donations", label: "Donations" },
  { to: "/admin/jobs", label: "Jobs" },
  { to: "/admin/stories", label: "Stories" },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/cms", label: "CMS" },
];

const isActive = (pathname, link) =>
  link.end ? pathname === link.to : pathname.startsWith(link.to);

export const AdminLayout = ({ title, children }) => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const navLink = (link, onClick) => (
    <Link
      key={link.to}
      to={link.to}
      onClick={onClick}
      className={`block px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
        isActive(location.pathname, link)
          ? "bg-white text-terracotta-700"
          : "text-white/90 hover:bg-white/15"
      }`}
    >
      {link.label}
    </Link>
  );

  return (
    <div className="min-h-screen bg-paper bg-grain [background-size:22px_22px]">
      <header className="bg-terracotta-700 text-white shadow-soft sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 min-w-0">
              <Link
                to="/admin"
                className="font-display text-lg font-semibold whitespace-nowrap flex items-center gap-2"
              >
                <span className="text-yellow-300">✝</span>
                <span className="hidden sm:inline">Compassion Admin</span>
              </Link>
              {title && (
                <>
                  <span className="text-white/40 hidden md:inline">/</span>
                  <span className="text-white/90 font-medium truncate hidden md:inline">
                    {title}
                  </span>
                </>
              )}
            </div>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((l) => navLink(l))}
              {/* User dropdown */}
              <div className="relative ml-2" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-white/15 hover:bg-white/25 transition-colors"
                  aria-expanded={userMenuOpen}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <span className="max-w-[140px] truncate">
                    {user?.email ?? "Account"}
                  </span>
                  <svg
                    className={`w-3 h-3 transition-transform duration-150 ${userMenuOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                    <Link
                      to="/admin/change-password"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Change Password
                    </Link>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </nav>

            {/* Burger (mobile/tablet) */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden text-2xl focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg px-2"
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <nav className="lg:hidden bg-terracotta-800 px-4 py-3 space-y-1">
            {NAV_LINKS.map((l) => navLink(l, () => setMenuOpen(false)))}
            <Link
              to="/admin/change-password"
              onClick={() => setMenuOpen(false)}
              className="block px-4 py-2 rounded-full text-sm font-semibold text-white/90 hover:bg-white/15 transition-colors"
            >
              Change Password
            </Link>
            <button
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
              className="block w-full text-left px-4 py-2 rounded-full text-sm font-semibold bg-ink-teal hover:bg-ink-teal/90 transition-colors"
            >
              Logout
            </button>
          </nav>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {title && (
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-terracotta-800 mb-6">
            {title}
          </h1>
        )}
        {children}
      </main>
    </div>
  );
};
